import React, { useRef } from 'react'
import { makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { useUsers } from '@paintscout/ui-business'
import { useDrag, useDrop } from 'react-dnd'
import classNames from 'classnames'
import type { CalendarEvent } from 'paintscout'
import { addAuth0Prefix } from '@paintscout/util'
import CrewSubRow from './CrewSubRow'
import type { ZoomLevel } from './TimelineHeader'

const useStyles = makeStyles<Theme, ProjectManagerRowProps>(
  (theme) => ({
    root: {
      // border-b border-slate-200/60 transition-all
      borderBottom: '1px solid rgba(203,213,225,0.6)', // border-slate-200/60
      transition: 'all 0.2s'
    },
    draggingRoot: {
      opacity: 0.5
    },
    overRoot: {
      // border-blue-500 border-2
      border: `2px solid ${theme.palette.primary.main}`
    },
    headerRow: {
      display: 'flex',
      backgroundColor: 'rgba(241,245,249,0.5)', // bg-slate-100/50
      borderTop: '1px solid rgba(203,213,225,0.4)', // border-y border-slate-300/40
      borderBottom: '1px solid rgba(203,213,225,0.4)',
      height: '44px'
    },
    headerSpan: {
      flex: 1,
      backgroundColor: 'rgba(241,245,249,0.3)' // bg-slate-100/30
    }
  }),
  { name: 'LeadRow' }
)

export interface ProjectManagerRowProps {
  classes?: StyleClasses<typeof useStyles>
  projectManagerId: string
  events?: CalendarEvent[]
  startDate: Date
  endDate: Date
  onProjectHover: (projectId: string | null) => void
  hoveredProject: string | null
  hoveredDate?: string | null
  onDateHover?: (date: string | null) => void
  onDrop: (eventId: string, crewId: string, newStartDate: Date) => void
  zoomLevel: ZoomLevel
  cellWidth: number
  onEventUpdate: (event: CalendarEvent) => void
  onMoveProjectManager?: (draggedProjectManagerId: string, targetProjectManagerId: string) => void
  onMoveCrewWithinProjectManager?: (projectManagerId: string, draggedCrewId: string, targetCrewId: string) => void
  crewOrder?: string[]
  crewIds?: string[]
  isExpanded?: boolean // Controlled expand/collapse state
}

function ProjectManagerRow(props: ProjectManagerRowProps) {
  const classes = useStyles(props)
  const {
    projectManagerId,
    startDate,
    endDate,
    onProjectHover,
    hoveredProject,
    hoveredDate,
    onDrop,
    zoomLevel,
    cellWidth,
    onEventUpdate,
    crewOrder = [],
    crewIds = [],
    isExpanded = true,
    onMoveProjectManager,
    events = []
  } = props
  const { users } = useUsers()
  const projectManager = users.find((l) => addAuth0Prefix(l.user_id) === projectManagerId)
  const ref = useRef<HTMLDivElement>(null)

  // Drag and drop for lead reordering (dragging disabled for now)
  const [{ isDragging }, preview] = useDrag({
    type: 'LEAD',
    item: { projectManagerId },
    canDrag: () => false,
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })

  const [{ isOver }, drop] = useDrop({
    accept: 'LEAD',
    drop: (item: { projectManagerId: string }) => {
      if (item.projectManagerId !== projectManagerId && onMoveProjectManager) {
        onMoveProjectManager(item.projectManagerId, projectManagerId)
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver()
    })
  })

  preview(drop(ref))

  if (!projectManager) return null

  // Group events by crew
  const eventsByCrewId = events.reduce(
    (acc, event) => {
      const crewId = event.crew || 'no-crew'
      if (!acc[crewId]) {
        acc[crewId] = []
      }
      acc[crewId].push(event)
      return acc
    },
    {} as Record<string, CalendarEvent[]>
  )

  // Get unique crew IDs for this lead's events
  const eventCrewIds = Object.keys(eventsByCrewId).sort((a, b) => {
    // Put "no-crew" at the end
    if (a === 'no-crew') return 1
    if (b === 'no-crew') return -1
    return a.localeCompare(b)
  })

  // Always render known crew rows even when they have no events.
  let mergedCrewIds = [...crewIds]
  const nonAssignedEventCrewIds = eventCrewIds.filter((id) => id !== 'no-crew' && !mergedCrewIds.includes(id))
  mergedCrewIds = [...mergedCrewIds, ...nonAssignedEventCrewIds]
  if (eventCrewIds.includes('no-crew')) {
    mergedCrewIds.push('no-crew')
  }

  // Apply custom crew ordering if available
  if (crewOrder.length > 0) {
    const orderedCrews = crewOrder.filter((id) => mergedCrewIds.includes(id))
    const unorderedCrews = mergedCrewIds.filter((id) => !crewOrder.includes(id))
    mergedCrewIds = [...orderedCrews, ...unorderedCrews]
  }

  return (
    <div
      ref={ref}
      className={classNames({ [classes.root]: true, [classes.draggingRoot]: isDragging, [classes.overRoot]: isOver })}
    >
      {/* Lead Header Row - Minimal thin header style */}
      <div className={classes.headerRow}>
        {/* Full-width horizontal span */}
        <div className={classes.headerSpan}></div>
      </div>
      {isExpanded &&
        mergedCrewIds.map((crewId) => {
          const crewEvents = eventsByCrewId[crewId] || []
          return (
            <CrewSubRow
              key={crewId}
              crewId={crewId}
              events={crewEvents}
              startDate={startDate}
              endDate={endDate}
              onProjectHover={onProjectHover}
              hoveredProject={hoveredProject}
              hoveredDate={hoveredDate}
              onDrop={onDrop}
              zoomLevel={zoomLevel}
              cellWidth={cellWidth}
              onEventUpdate={onEventUpdate}
              parentProjectManagerId={projectManagerId}
            />
          )
        })}
      {/* {isExpanded &&
        crewIds.map((crewId) => {
          const crewProjects = projectsByCrewId[crewId]
          return (
            <CrewSubRow
              key={crewId}
              crewId={crewId}
              projects={crewProjects}
              startDate={startDate}
              endDate={endDate}
              onProjectHover={onProjectHover}
              hoveredProject={hoveredProject}
              hoveredDate={hoveredDate}
              onDrop={onDrop}
              zoomLevel={zoomLevel}
              onEventUpdate={onEventUpdate}
              onEventDelete={onEventDelete}
              parentLeadId={projectManagerId}
            />
          )
        })} */}
    </div>
  )
}

export default ProjectManagerRow
